import type { RawMessage } from "@/api/messages";
import { Button } from "@penmate/react-shared/ui/button";
import {
  DocumentDialog,
  DocumentDialogClose,
  DocumentDialogContent,
  DocumentDialogDescription,
  DocumentDialogTitle,
} from "@/components/ui/document-dialog";
import {
  StripeAddressElement,
  type StripeAddressElementHandle,
  type StripeAddressValue,
} from "@/components/stripe/StripeAddressElement";
import { useEffect, useMemo, useRef, useState } from "react";
import { useAutoAnimate } from "@formkit/auto-animate/react";

const getReturnAddress = (letter: RawMessage | null): any | null => {
  const ra = (letter as any)?.returnAddress ?? (letter as any)?.data?.returnAddress;
  return ra && typeof ra === "object" ? ra : null;
};

const getDefaultReturnAddress = (letter: RawMessage | null): any | null => {
  const v =
    (letter as any)?.defaultReturnAddress ??
    (letter as any)?.default_from_address ??
    (letter as any)?.data?.defaultReturnAddress ??
    (letter as any)?.data?.default_from_address;
  return v && typeof v === "object" ? v : null;
};

const hasLine1 = (addr: any | null): boolean => {
  if (!addr) return false;
  const l1 = addr.address_line1 ?? addr.line1;
  return typeof l1 === "string" && l1.trim().length > 0;
};

const formatAddressLines = (raw: any | null): string[] => {
  if (!raw || typeof raw !== "object") return [];
  const name =
    typeof raw.name === "string"
      ? raw.name
      : typeof raw.firstName === "string" && typeof raw.lastName === "string"
        ? `${raw.firstName} ${raw.lastName}`.trim()
        : typeof raw.first_name === "string" && typeof raw.last_name === "string"
          ? `${raw.first_name} ${raw.last_name}`.trim()
          : "";
  const line1 = typeof raw.address_line1 === "string" ? raw.address_line1 : typeof raw.line1 === "string" ? raw.line1 : "";
  const line2 = typeof raw.address_line2 === "string" ? raw.address_line2 : typeof raw.line2 === "string" ? raw.line2 : "";
  const city = typeof raw.address_city === "string" ? raw.address_city : typeof raw.city === "string" ? raw.city : "";
  const state = typeof raw.address_state === "string" ? raw.address_state : typeof raw.state === "string" ? raw.state : "";
  const zip = typeof raw.address_zip === "string" ? raw.address_zip : typeof raw.postal_code === "string" ? raw.postal_code : "";
  const country =
    typeof raw.address_country === "string"
      ? raw.address_country
      : typeof raw.country === "string"
        ? raw.country
        : "";

  const cityLine = [city, state, zip].filter(Boolean).join(", ").replace(", ,", ",").trim();
  const lines = [name, line1, line2, cityLine, country.toUpperCase()].filter((l) => typeof l === "string" && l.trim().length > 0);
  return lines as string[];
};

const toStripeAddressValue = (raw: any | null): StripeAddressValue => {
  if (!raw || typeof raw !== "object") return {};
  return {
    name: typeof raw.name === "string" ? raw.name : undefined,
    firstName: typeof raw.firstName === "string" ? raw.firstName : typeof raw.first_name === "string" ? raw.first_name : undefined,
    lastName: typeof raw.lastName === "string" ? raw.lastName : typeof raw.last_name === "string" ? raw.last_name : undefined,
    address_line1: typeof raw.address_line1 === "string" ? raw.address_line1 : typeof raw.line1 === "string" ? raw.line1 : undefined,
    address_line2: typeof raw.address_line2 === "string" ? raw.address_line2 : typeof raw.line2 === "string" ? raw.line2 : undefined,
    address_city: typeof raw.address_city === "string" ? raw.address_city : typeof raw.city === "string" ? raw.city : undefined,
    address_state: typeof raw.address_state === "string" ? raw.address_state : typeof raw.state === "string" ? raw.state : undefined,
    address_zip: typeof raw.address_zip === "string" ? raw.address_zip : typeof raw.postal_code === "string" ? raw.postal_code : undefined,
    address_country: typeof raw.address_country === "string" ? raw.address_country : typeof raw.country === "string" ? raw.country : undefined,
  };
};

export function ReturnAddressSheet(props: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  checkoutLetter: RawMessage | null;
  onSave: (args: { returnAddress: StripeAddressValue }) => Promise<void>;
}) {
  const { open, onOpenChange, checkoutLetter, onSave } = props;
  const [contentAutoAnimateRef] = useAutoAnimate<HTMLDivElement>();

  const existingReturnAddress = useMemo(() => getReturnAddress(checkoutLetter), [checkoutLetter]);
  const defaultReturnAddress = useMemo(() => getDefaultReturnAddress(checkoutLetter), [checkoutLetter]);

  const initialDefault = useMemo(() => {
    if (hasLine1(existingReturnAddress)) return toStripeAddressValue(existingReturnAddress);
    if (hasLine1(defaultReturnAddress)) return toStripeAddressValue(defaultReturnAddress);
    return {};
  }, [defaultReturnAddress, existingReturnAddress]);

  const [elementKey, setElementKey] = useState(0);
  const [defaultAddress, setDefaultAddress] = useState<StripeAddressValue>(initialDefault);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [showEditor, setShowEditor] = useState(false);
  const stripeRef = useRef<StripeAddressElementHandle | null>(null);

  const contentHeightClass = showEditor
    ? "h-[calc(100vh-2rem)] supports-[height:100dvh]:h-[calc(100dvh-2rem)]"
    : "h-auto";

  useEffect(() => {
    if (!open) return;
    setDefaultAddress(initialDefault);
    setElementKey((k) => k + 1);
    setSaveError(null);
    setIsSaving(false);
    setShowEditor(true);
  }, [initialDefault, open, (checkoutLetter as any)?.id]);

  const hasDefaultShortcut = hasLine1(defaultReturnAddress);
  const currentLines = useMemo(() => formatAddressLines(existingReturnAddress), [existingReturnAddress]);
  const hasCurrent = currentLines.length > 0;

  useEffect(() => {
    if (!open) return;
    setShowEditor(!hasCurrent);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, hasCurrent]);

  const save = async () => {
    setSaveError(null);
    const api = stripeRef.current;
    if (!api) {
      setSaveError("Tap Edit to update your return address.");
      return;
    }

    setIsSaving(true);
    try {
      await api.triggerValidation();
      const v = await api.getValue();
      if (!v || !v.complete) {
        setIsSaving(false);
        return;
      }
      await onSave({ returnAddress: v.value });
      onOpenChange(false);
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : String(e));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    // Disable Radix scroll-lock: it can prevent scrolling in fixed/fullscreen overlays on mobile WebViews.
    <DocumentDialog open={open} onOpenChange={onOpenChange} modal={false}>
      <DocumentDialogContent
        className={`w-[calc(100vw-1rem)] max-w-md !p-0 ${contentHeightClass} max-h-[calc(100vh-2rem)] supports-[height:100dvh]:max-h-[calc(100dvh-2rem)] overflow-hidden sm:h-auto sm:max-h-[calc(100vh-2rem)]`}
      >
        <DocumentDialogTitle className="sr-only">Return Address</DocumentDialogTitle>
        <DocumentDialogDescription className="sr-only">Add your return address.</DocumentDialogDescription>

        <div className={`flex min-h-0 flex-col ${showEditor ? "h-full" : ""}`}>
          <div className="flex items-center justify-between border-b px-4 py-3">
            <div className="min-w-0">
              <div className="text-sm font-semibold">Return address</div>
              <div className="mt-0.5 text-xs text-muted-foreground">
                All messages require a return address to complete delivery.
              </div>
            </div>
          </div>

          <div
            className={`${showEditor ? "min-h-0 flex-1 overflow-y-auto" : ""} px-4 py-4`}
            style={{
              WebkitOverflowScrolling: "touch",
              overscrollBehavior: "contain",
              touchAction: "pan-y",
            }}
            ref={contentAutoAnimateRef}
          >
            {hasCurrent ? (
              <div className="mb-3 rounded-xl border border-input bg-white/70 px-3 py-2 shadow-sm">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-xs font-medium text-muted-foreground">Current Return Address</div>
                    <div className="ph-mask mt-1 space-y-0.5 text-sm">
                      {currentLines.map((l) => (
                        <div key={l} className="truncate">
                          {l}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ) : null}

            {!showEditor ? (
              <div className="space-y-2">
                <Button
                  type="button"
                  className="w-full"
                  onClick={() => setShowEditor(true)}
                  data-testid="return-address-edit"
                >
                  Edit
                </Button>
                {hasDefaultShortcut ? (
                  <Button
                    type="button"
                    variant="secondary"
                    className="w-full"
                    onClick={() => {
                      setDefaultAddress(toStripeAddressValue(defaultReturnAddress));
                      setElementKey((k) => k + 1);
                      setShowEditor(true);
                    }}
                    data-testid="return-address-use-default"
                  >
                    Use default address
                  </Button>
                ) : null}
              </div>
            ) : null}

            {showEditor ? (
              <div>
                {hasDefaultShortcut ? (
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    className="mb-3 w-full"
                    onClick={() => {
                      setDefaultAddress(toStripeAddressValue(defaultReturnAddress));
                      setElementKey((k) => k + 1);
                    }}
                    data-testid="return-address-use-default"
                  >
                    Use default address
                  </Button>
                ) : null}

                <div className="ph-no-capture rounded-xl border border-input bg-white/70 p-3 shadow-sm">
                  <StripeAddressElement
                    key={elementKey}
                    ref={(v) => {
                      stripeRef.current = v;
                    }}
                    defaultAddress={defaultAddress}
                    debounceMs={300}
                  />
                </div>
              </div>
            ) : null}

            {saveError ? <div className="mt-3 text-xs text-destructive">{saveError}</div> : null}
          </div>

          <div className="flex items-center justify-end gap-2 border-t px-4 py-3">
            <DocumentDialogClose asChild>
              <Button type="button" variant="ghost" disabled={isSaving}>
                Cancel
              </Button>
            </DocumentDialogClose>
            <Button
              type="button"
              onClick={() => void save()}
              disabled={isSaving || !showEditor}
              data-testid="return-address-save"
            >
              Save & Continue
            </Button>
          </div>
        </div>
      </DocumentDialogContent>
    </DocumentDialog>
  );
}
